/**
 * MobileUploadsPanel — Operator-side floating panel for managing
 * upload links and reviewing field uploads from technicians.
 *
 * Features:
 * - Generate / share / revoke upload links
 * - View incoming uploads with thumbnails
 * - Place uploads on map (auto-GPS or manual click)
 * - Filter by status (All / Pending / Placed)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useProjectStore, type Indication } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import { useMapStore } from '../../stores/mapStore';
import {
  createUploadLink,
  listUploadLinks,
  updateUploadLink,
  deleteUploadLink,
  listFieldUploads,
  updateUploadStatus,
  deleteFieldUpload,
  buildAssetUrl,
  buildUploadPageUrl,
  type UploadLink,
  type FieldUpload,
} from '../../services/mobileUploadApi';
import { rectGeomFromCenter, rotatePolygonAroundCenter, defaultSizeForType } from '../../utils/geometry';
import ShareLinkModal from './ShareLinkModal';
import { deriveTopologyIntoIndication } from '../../utils/createIndication';
import './MobileUploadsPanel.css';

// ─── Props ──────────────────────────────────────────────────────────────────

interface MobileUploadsPanelProps {
  onClose: () => void;
}

type StatusFilter = 'all' | 'pending' | 'placed';

// ─── Component ──────────────────────────────────────────────────────────────

export default function MobileUploadsPanel({ onClose }: MobileUploadsPanelProps) {
  const siteId = useProjectStore((s) => s.meta.siteId);
  const siteName = useProjectStore((s) => s.meta.siteName);
  const addIndication = useProjectStore((s) => s.addIndication);
  const updateAvailableDates = useProjectStore((s) => s.updateAvailableDates);
  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const setAssetPlaceCallback = useUIStore((s) => s.setAssetPlaceCallback);
  const flyTo = useMapStore((s) => s.flyTo);
  const indications = useProjectStore((s) => s.data.indications);

  // ── State ──
  const [links, setLinks] = useState<UploadLink[]>([]);
  const [uploads, setUploads] = useState<FieldUpload[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(false);
  const [placingUpload, setPlacingUpload] = useState<FieldUpload | null>(null);
  const [previewUpload, setPreviewUpload] = useState<FieldUpload | null>(null);
  const [failedThumbs, setFailedThumbs] = useState<Set<string>>(new Set());

  // Generate link form
  const [linkLabel, setLinkLabel] = useState('');
  const [linkExpiry, setLinkExpiry] = useState('24');
  const [generating, setGenerating] = useState(false);

  // Share modal
  const [shareModal, setShareModal] = useState<{
    url: string;
    label: string | null;
    expiresAt: string | null;
  } | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Load data ──
  const loadData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [linksData, uploadsData] = await Promise.all([
        listUploadLinks(siteId),
        listFieldUploads(siteId, { limit: 100 }),
      ]);
      if (mountedRef.current) {
        setLinks(linksData);
        setUploads(uploadsData.items);
      }
    } catch (err) {
      console.error('Failed to load upload data:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Detect when a placed upload's indication is removed from the map ──
  const indicationIds = useMemo(() => new Set(indications.map((i) => i.id)), [indications]);

  useEffect(() => {
    setUploads((prev) => {
      let changed = false;
      const next = prev.map((u) => {
        if (u.status === 'placed' && u.placed_indication_id && !indicationIds.has(u.placed_indication_id)) {
          changed = true;
          // Revert to pending locally; also tell server
          if (siteId) {
            updateUploadStatus(siteId, u.id, 'pending').catch(() => {});
          }
          return { ...u, status: 'pending' as const, placed_indication_id: null };
        }
        return u;
      });
      return changed ? next : prev;
    });
  }, [indicationIds, siteId]);

  // ── ESC key to cancel place mode ──
  useEffect(() => {
    if (!placingUpload) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPlacingUpload(null);
        setAssetPlaceCallback(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [placingUpload, setAssetPlaceCallback]);

  // ── Generate upload link ──
  const handleGenerateLink = async () => {
    if (!siteId) return;
    setGenerating(true);
    try {
      await createUploadLink(siteId, {
        label: linkLabel || undefined,
        expiresInHours: parseInt(linkExpiry, 10) || 24,
      });
      setLinkLabel('');
      await loadData();
      addNotification({ type: 'success', title: 'Upload link created' });
    } catch (err) {
      addNotification({ type: 'error', title: 'Failed to create link', message: String(err) });
    } finally {
      setGenerating(false);
    }
  };

  // ── Toggle link active ──
  const handleToggleLink = async (link: UploadLink) => {
    try {
      await updateUploadLink(link.id, { isActive: link.is_active !== 1 });
      await loadData();
    } catch (_err) {
      addNotification({ type: 'error', title: 'Failed to update link' });
    }
  };

  // ── Delete link ──
  const handleDeleteLink = (link: UploadLink) => {
    showConfirm({
      title: 'Delete Upload Link',
      message: `Delete "${link.label || 'Unnamed link'}"? Technicians will no longer be able to upload via this link.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await deleteUploadLink(link.id);
          await loadData();
          addNotification({ type: 'success', title: 'Link deleted' });
        } catch {
          addNotification({ type: 'error', title: 'Failed to delete link' });
        }
      },
    });
  };

  // ── Place upload on map ──
  const handleAddToMap = (upload: FieldUpload) => {
    const hasGps = upload.exif_lat != null && upload.exif_lon != null;

    if (hasGps) {
      // Auto-place at GPS coordinates
      placeOnMap(upload, upload.exif_lon!, upload.exif_lat!);
    } else {
      // Enter manual place mode
      setPlacingUpload(upload);
      setAssetPlaceCallback((lngLat) => {
        placeOnMap(upload, lngLat.lng, lngLat.lat);
        setPlacingUpload(null);
        setAssetPlaceCallback(null);
      });
    }
  };

  const placeOnMap = async (upload: FieldUpload, lng: number, lat: number) => {
    const isVideo = upload.content_type.startsWith('video/');
    const type = isVideo ? 'Video' : 'Photo';
    const size = defaultSizeForType(type);
    let geom = rectGeomFromCenter(lng, lat, size.w, size.h);

    if (upload.exif_bearing != null) {
      geom = rotatePolygonAroundCenter(geom, [lng, lat], upload.exif_bearing);
    }

    const assetUrl = buildAssetUrl(upload.r2_key);
    const now = new Date().toISOString();
    const dateOnly = upload.exif_capture_date || now.split('T')[0];
    const indicationId = `field-${upload.id}`;

    const indication: Indication = {
      id: indicationId,
      type: type as 'Photo' | 'Video',
      geometry: geom,
      properties: {
        title: upload.original_name,
        image: assetUrl,
        notes: [
          upload.note,
          upload.exif_lat != null ? `GPS: ${upload.exif_lat.toFixed(6)}, ${upload.exif_lon?.toFixed(6)}` : null,
          upload.exif_capture_date ? `Captured: ${upload.exif_capture_date}` : null,
        ]
          .filter(Boolean)
          .join('\n'),
        captureDate: dateOnly,
        rotationDeg: upload.exif_bearing ?? 0,
        createdAt: now,
        updatedAt: now,
      },
    };

    // Topology from the GRID only (owner ruling 2026-09-02) — the derive on
    // the placed rect, with provenance; nothing when no cell answers. The
    // old path proximity-filled behind a confidence>0.3 gate no other
    // surface used, and silently dropped customFields.
    const placed = deriveTopologyIntoIndication(indication);

    addIndication(placed);
    updateAvailableDates();

    // Fly map to placed location
    flyTo([lng, lat], 19);

    // Optimistically update local state so UI reflects "placed" immediately
    setUploads((prev) =>
      prev.map((u) =>
        u.id === upload.id ? { ...u, status: 'placed' as const, placed_indication_id: indicationId } : u
      )
    );

    // Persist status to server
    if (siteId) {
      try {
        await updateUploadStatus(siteId, upload.id, 'placed', indicationId);
      } catch (err) {
        console.error('Failed to update upload status to placed:', err);
        // Optimistic update already applied — server will catch up on next save/reload
      }
    }

    addNotification({
      type: 'success',
      title: `${upload.original_name} placed on map`,
    });
  };

  // ── Delete upload ──
  const handleDeleteUpload = (upload: FieldUpload) => {
    showConfirm({
      title: 'Delete Upload',
      message: `Delete "${upload.original_name}"? This will also remove the file from storage.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        if (!siteId) return;
        try {
          await deleteFieldUpload(siteId, upload.id);
          await loadData();
          addNotification({ type: 'success', title: 'Upload deleted' });
        } catch {
          addNotification({ type: 'error', title: 'Failed to delete upload' });
        }
      },
    });
  };

  // ── Derived (client-side filtering) ──
  const pendingCount = uploads.filter((u) => u.status === 'pending').length;
  const placedCount = uploads.filter((u) => u.status === 'placed').length;
  const filteredUploads = statusFilter === 'all'
    ? uploads
    : uploads.filter((u) => u.status === statusFilter);
  const activeLink = links.find((l) => l.is_active === 1);

  // ── Render ──

  if (!siteId) {
    return (
      <div className="mu-panel">
        <div className="mu-header">
          <span className="mu-header__title">Mobile Uploads</span>
          <button className="mu-header__btn" onClick={onClose}>×</button>
        </div>
        <div className="mu-body">
          <div className="mu-no-site">
            Save the project to a server site to enable mobile uploads.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mu-panel">
      {/* Header */}
      <div className="mu-header">
        <span className="mu-header__title">Mobile Uploads</span>
        {pendingCount > 0 && <span className="mu-badge">{pendingCount}</span>}
        <button className="mu-header__btn" onClick={loadData} title="Refresh">
          &#8635;
        </button>
        <button className="mu-header__btn" onClick={onClose}>×</button>
      </div>

      <div className="mu-body">
        {/* Upload Link Section */}
        <div className="mu-link-section">
          <div className="mu-link-section__title">Upload Link</div>

          {activeLink ? (
            <div className="mu-active-link">
              <div className="mu-active-link__info">
                <span
                  className={`mu-active-link__status ${
                    activeLink.is_active === 1
                      ? 'mu-active-link__status--active'
                      : 'mu-active-link__status--paused'
                  }`}
                />
                <span className="mu-active-link__label">
                  {activeLink.label || 'Upload Link'}
                </span>
                <span className="mu-active-link__count">
                  {activeLink.upload_count}
                  {activeLink.max_uploads != null ? `/${activeLink.max_uploads}` : ''} uploads
                </span>
              </div>
              <div className="mu-active-link__actions">
                <button
                  className="mu-link-btn mu-link-btn--share"
                  onClick={() =>
                    setShareModal({
                      url: buildUploadPageUrl(activeLink.token),
                      label: activeLink.label,
                      expiresAt: activeLink.expires_at,
                    })
                  }
                >
                  Share
                </button>
                <button
                  className="mu-link-btn"
                  onClick={() => handleToggleLink(activeLink)}
                >
                  {activeLink.is_active === 1 ? 'Pause' : 'Resume'}
                </button>
                <button
                  className="mu-link-btn mu-link-btn--danger"
                  onClick={() => handleDeleteLink(activeLink)}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="mu-generate-form">
              <div className="mu-generate-form__row">
                <input
                  type="text"
                  placeholder="Label (e.g. Field Team A)"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                />
                <select value={linkExpiry} onChange={(e) => setLinkExpiry(e.target.value)}>
                  <option value="1">1 hour</option>
                  <option value="6">6 hours</option>
                  <option value="24">24 hours</option>
                  <option value="72">3 days</option>
                  <option value="168">7 days</option>
                  <option value="720">30 days</option>
                </select>
              </div>
              <button
                className="mu-generate-btn"
                onClick={handleGenerateLink}
                disabled={generating}
              >
                {generating ? 'Creating...' : 'Generate Upload Link'}
              </button>
            </div>
          )}
        </div>

        {/* Place Mode Bar */}
        {placingUpload && (
          <div className="mu-place-bar">
            <span>Click map to place: {placingUpload.original_name}</span>
            <button
              className="mu-place-bar__cancel"
              onClick={() => {
                setPlacingUpload(null);
                setAssetPlaceCallback(null);
              }}
            >
              ESC Cancel
            </button>
          </div>
        )}

        {/* Status Filter */}
        <div className="mu-filter-tabs">
          <button
            className={`mu-filter-tab ${statusFilter === 'all' ? 'mu-filter-tab--active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({uploads.length})
          </button>
          <button
            className={`mu-filter-tab ${statusFilter === 'pending' ? 'mu-filter-tab--active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={`mu-filter-tab ${statusFilter === 'placed' ? 'mu-filter-tab--active' : ''}`}
            onClick={() => setStatusFilter('placed')}
          >
            Placed ({placedCount})
          </button>
        </div>

        {/* Upload List */}
        {loading && uploads.length === 0 ? (
          <div className="mu-empty">Loading...</div>
        ) : filteredUploads.length === 0 ? (
          <div className="mu-empty">
            {statusFilter === 'all'
              ? 'No uploads yet. Share the link with a field technician.'
              : `No ${statusFilter} uploads.`}
          </div>
        ) : (
          <div className="mu-uploads-list">
            {filteredUploads.map((upload) => {
              const isImage = upload.content_type.startsWith('image/');
              const hasGps = upload.exif_lat != null && upload.exif_lon != null;
              const isPlaced = upload.status === 'placed';

              return (
                <div
                  key={upload.id}
                  className={`mu-upload-card${isPlaced ? ' mu-upload-card--placed' : ''}`}
                >
                  {/* Thumbnail — click to preview */}
                  {isImage && !failedThumbs.has(upload.id) ? (
                    <img
                      src={buildAssetUrl(upload.r2_key)}
                      alt=""
                      className="mu-upload-card__thumb mu-upload-card__thumb--clickable"
                      loading="lazy"
                      onClick={() => setPreviewUpload(upload)}
                      title="Click to preview"
                      onError={() => setFailedThumbs((prev) => new Set(prev).add(upload.id))}
                    />
                  ) : (
                    <div
                      className={`mu-upload-card__thumb ${isImage ? 'mu-upload-card__thumb--fallback' : 'mu-upload-card__thumb--video'} mu-upload-card__thumb--clickable`}
                      onClick={() => setPreviewUpload(upload)}
                      title={isImage ? 'Click to preview' : 'Click to play'}
                    >
                      {isImage ? '\uD83D\uDDBC' : '\u25B6'}
                    </div>
                  )}

                  {/* Info */}
                  <div className="mu-upload-card__info">
                    <div className="mu-upload-card__name">{upload.original_name}</div>
                    <div className="mu-upload-card__meta">
                      <span className={`mu-gps-badge ${hasGps ? 'mu-gps-badge--yes' : 'mu-gps-badge--no'}`}>
                        {hasGps ? 'GPS' : 'No GPS'}
                      </span>
                      {isPlaced && (
                        <span className="mu-status-badge mu-status-badge--placed">Placed</span>
                      )}
                      <span>
                        {new Date(upload.created_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    {upload.note && (
                      <div className="mu-upload-card__meta" style={{ marginTop: 2 }}>
                        {upload.note}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mu-upload-card__actions">
                    {!isPlaced && (
                      <button
                        className="mu-card-btn mu-card-btn--primary"
                        onClick={() => handleAddToMap(upload)}
                        title={hasGps ? 'Auto-place at GPS' : 'Click map to place'}
                      >
                        {hasGps ? 'Add (GPS)' : 'Add'}
                      </button>
                    )}
                    {hasGps && (
                      <button
                        className="mu-card-btn mu-card-btn--locate"
                        onClick={() => flyTo([upload.exif_lon!, upload.exif_lat!], 19)}
                        title="Zoom to GPS location"
                      >
                        &#8982; Locate
                      </button>
                    )}
                    <button
                      className="mu-card-btn mu-card-btn--danger"
                      onClick={() => handleDeleteUpload(upload)}
                      title="Delete upload"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview Overlay (Image or Video) */}
      {previewUpload && (
        <div className="mu-preview-overlay" onClick={() => setPreviewUpload(null)}>
          <div className="mu-preview-overlay__content" onClick={(e) => e.stopPropagation()}>
            {previewUpload.content_type.startsWith('video/') ? (
              <video
                src={buildAssetUrl(previewUpload.r2_key)}
                className="mu-preview-overlay__video"
                controls
                autoPlay
              />
            ) : (
              <img
                src={buildAssetUrl(previewUpload.r2_key)}
                alt={previewUpload.original_name}
                className="mu-preview-overlay__img"
              />
            )}
            <div className="mu-preview-overlay__footer">
              <span className="mu-preview-overlay__name">{previewUpload.original_name}</span>
              <button
                className="mu-preview-overlay__close"
                onClick={() => setPreviewUpload(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModal && (
        <ShareLinkModal
          isOpen
          onClose={() => setShareModal(null)}
          url={shareModal.url}
          siteName={siteName || 'Site'}
          label={shareModal.label}
          expiresAt={shareModal.expiresAt}
        />
      )}
    </div>
  );
}
